export function getCookie<T>(name: string, defaultValue: T): T {
    const nameEQ = name + "=";
    const cookies = document.cookie.split(";");

    for (let i = 0; i < cookies.length; i++) {
        const cookie = cookies[i].trim();
        if (cookie.indexOf(nameEQ) === 0) {
            const cookieStr = cookie.substring(nameEQ.length, cookie.length);
            try {
                const decoded = decodeURIComponent(cookieStr);
                return JSON.parse(decoded);
            } catch (_e) {
                return cookieStr as T;
            }
        }
    }
    return defaultValue;
}

/**
 * Derived from the current host rather than a build-time env var, so this always
 * agrees with what the server issues (see App\Services\Cookies\FrontendCookieJar).
 * A baked-in domain that does not match the host it is served on silently falls
 * back to a host-only cookie, which then shadows the server's domain-wide one.
 *
 * Assumes a two-label registrable domain, which holds for every host this
 * application serves. A host under a multi-label public suffix (foo.co.uk) would
 * produce a domain browsers reject.
 */
export function encompassingCookieDomain(hostname: string): string | null {
    if (/^\d{1,3}(\.\d{1,3}){3}$/.test(hostname)) {
        return null;
    }

    const labels = hostname.split(".");
    if (labels.length < 2) {
        return null;
    }

    return "." + labels.slice(-2).join(".");
}

export function cookieDomainAttribute(): string {
    const domain = encompassingCookieDomain(window.location.hostname);

    return domain ? ";domain=" + domain : "";
}

export function setCookie<T>(cname: string, val: T, daysToExpiry = 1000) {
    const d = new Date();
    d.setTime(d.getTime() + daysToExpiry * 24 * 60 * 60 * 1000);
    const expires = "expires=" + d.toUTCString();
    const domainAttribute = cookieDomainAttribute();
    document.cookie =
        cname +
        "=" +
        encodeURIComponent(JSON.stringify(val)) +
        ";" +
        expires +
        ";path=/" +
        domainAttribute;

    if (domainAttribute) {
        expireLegacyHostOnlyCookie(cname);
    }
}

// A leftover host-only cookie with the same name shadows the domain-wide one,
// so reads would keep returning the stale value until it is expired.
function expireLegacyHostOnlyCookie(cname: string) {
    document.cookie = cname + "=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/";
}
